import * as Dialog from '@radix-ui/react-dialog';
import { AnimatePresence, motion } from 'framer-motion';
import { Lock, X } from 'lucide-react';
import {
  airingLine,
  badgeOfKind,
  formatClockTime,
  isLive,
  looseBadges,
  monogram,
  actionTarget,
} from '@/lib/card';
import type { MediaAction, MediaCard } from '@/protocol/media';
import { Pill } from '../ui';

export function CardDialog({ card, onClose }: { card: MediaCard | null; onClose: () => void }) {
  return (
    <Dialog.Root open={card !== null} onOpenChange={(open) => !open && onClose()}>
      <AnimatePresence>
        {card && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="bg-shell/80 fixed inset-0 z-40 backdrop-blur-sm"
              />
            </Dialog.Overlay>

            <Dialog.Content asChild>
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 12, scale: 0.98 }}
                transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
                className="border-line bg-surface rounded-panel scroll-slim fixed top-1/2 left-1/2 z-50 max-h-[88vh] w-[min(760px,94vw)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto border shadow-2xl"
              >
                <Body card={card} />
                <Dialog.Close asChild>
                  <button
                    type="button"
                    aria-label="Close"
                    className="border-line bg-shell/70 text-muted hover:text-text absolute top-3 right-3 grid size-8 place-items-center rounded-full border backdrop-blur transition"
                  >
                    <X className="size-4" />
                  </button>
                </Dialog.Close>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}

function Body({ card }: { card: MediaCard }) {
  const locked = card.playable === false;
  const entitlement = badgeOfKind(card, 'entitlement');
  const airing = airingLine(card);
  const startAt = card.airing?.startTime ? formatClockTime(card.airing.startTime) : null;
  // The offer and the entitlement badge routinely carry the same word, and two identical pills side
  // by side read as a rendering bug rather than as two fields agreeing.
  const offer =
    card.offer && card.offer.toLowerCase() !== entitlement?.text.toLowerCase() ? card.offer : null;

  return (
    <>
      <div className="bg-raised relative aspect-video w-full overflow-hidden">
        {card.posterUrl ? (
          <img src={card.posterUrl} alt="" className="size-full object-cover" />
        ) : (
          <span className="text-faint grid size-full place-items-center text-5xl font-semibold tracking-widest">
            {monogram(card)}
          </span>
        )}
        <span className="from-surface absolute inset-0 bg-gradient-to-t via-transparent to-transparent" />
      </div>

      <div className="flex flex-col gap-4 p-5">
        <div className="flex flex-wrap items-center gap-1.5">
          {isLive(card) && <Pill tone="live">Live</Pill>}
          {entitlement && <Pill tone={locked ? 'locked' : 'positive'}>{entitlement.text}</Pill>}
          {offer && <Pill>{offer}</Pill>}
          {card.contentType && <Pill>{card.contentType}</Pill>}
          {looseBadges(card).map((badge) => (
            <Pill key={`${badge.kind}-${badge.text}`}>{badge.text}</Pill>
          ))}
        </div>

        <div>
          <Dialog.Title className="text-2xl leading-tight font-semibold tracking-tight">
            {card.title}
          </Dialog.Title>
          {card.subtitle && <p className="text-muted mt-1 text-sm">{card.subtitle}</p>}
          {airing && <p className="text-accent-soft mt-1 text-sm font-medium">{airing}</p>}
        </div>

        {locked && (
          <p className="border-warn/30 bg-warn/10 text-warn flex items-start gap-2 rounded-xl border px-3.5 py-2.5 text-sm">
            <Lock className="mt-0.5 size-4 shrink-0" />
            Not in this household's package. The agent surfaced it anyway, which is worth noting.
          </p>
        )}

        {card.synopsis && (
          <Dialog.Description className="text-muted text-sm leading-relaxed">
            {card.synopsis}
          </Dialog.Description>
        )}

        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <Fact label="Year" value={card.year} />
          <Fact label="Channel" value={card.airing?.channelName} />
          <Fact label="Channel number" value={card.airing?.channelNumber} />
          <Fact label="Starts" value={startAt} />
          <Fact label="Market" value={card.airing?.market} />
          <Fact label="Watching now" value={card.airing?.watchingNow} />
          <Fact label="Score" value={card.airing?.score} />
          <Fact label="Genres" value={card.genres?.join(', ')} />
          <Fact label="Director" value={card.director?.join(', ')} />
          <Fact label="Cast" value={card.cast?.join(', ')} />
          <Fact label="Moods" value={card.moods?.join(', ')} />
          <Fact label="Themes" value={card.themes?.join(', ')} />
          <Fact label="Why this" value={card.rankReason} />
        </dl>

        {card.actions && card.actions.length > 0 && <Actions actions={card.actions} />}

        <p className="text-faint font-mono text-[11px]">{card.id}</p>
      </div>
    </>
  );
}

function Fact({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="min-w-0">
      <dt className="text-faint text-[11px] font-semibold tracking-widest uppercase">{label}</dt>
      <dd className="text-muted">{value}</dd>
    </div>
  );
}

/**
 * The actions are shown but not performed: the agent only describes what selection should do, and
 * carrying it out — tuning, deep-linking, navigating — is the receiver's job. What matters here is
 * that the right action arrived with the right target.
 */
function Actions({ actions }: { actions: MediaAction[] }) {
  return (
    <div className="border-line-soft flex flex-col gap-2 border-t pt-4">
      <h4 className="text-faint text-[11px] font-semibold tracking-widest uppercase">
        Actions · handled by the receiver
      </h4>
      {actions.map((action, index) => {
        const target = actionTarget(action);
        return (
          <div
            key={`${action.action}-${index}`}
            className="border-line bg-shell/50 flex items-center gap-3 rounded-xl border px-3.5 py-2.5"
          >
            <Pill tone={action.primary ? 'accent' : 'neutral'}>{action.action}</Pill>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">
                {action.label ?? action.action}
              </span>
              {target && (
                <span className="text-faint block truncate font-mono text-[11px]">{target}</span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}
